import { DecimalPipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  OnDestroy,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatDividerModule } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatSlideToggle } from '@angular/material/slide-toggle';
import {
  AreaPipe,
  DistancePipe,
  IMeasureSegment,
  MapMeasurementService,
  MeasureType,
  ParcelSnapService,
  SegmentDistancePipe,
  TotalAreaPipe,
  TotalDistancePipe,
} from '@lassi/webapp-shared';

@Component({
  selector: 'lassi-measure-overlay',
  imports: [
    MatCardModule,
    MatDividerModule,
    MatIconModule,
    MatButtonModule,
    SegmentDistancePipe,
    TotalDistancePipe,
    TotalAreaPipe,
    DistancePipe,
    AreaPipe,
    MatSlideToggle,
    MatProgressSpinner,
    DecimalPipe,
  ],
  template: `
    @if (measureType()) {
      <mat-card class="measure-card">
        <mat-card-header>
          <div class="header">
            <mat-card-title>
              <h1>{{ measureType() }}</h1>
            </mat-card-title>
            <button class="close-button" mat-icon-button (click)="onClose()">
              <mat-icon>close</mat-icon>
            </button>
          </div>
        </mat-card-header>
        <mat-card-content class="content">
          <p class="mat-label-medium">Tap on the map to add to your path.</p>
          @if (hasSelfIntersection()) {
            <p class="error-hint mat-label-small">
              Cannot finish drawing due to self-intersects.
            </p>
          }
          <div class="snap-toggle-row">
            <mat-slide-toggle
              [checked]="snapEnabled()"
              [disabled]="isSnapDisabled()"
              (change)="onSnapToggle($event.checked)"
            >
              Snap to parcel boundaries
            </mat-slide-toggle>
            @if (isSnapLoading()) {
              <mat-spinner diameter="16" />
            }
          </div>
          @if (isTooZoomedOut()) {
            <p class="error-hint mat-label-small">
              @if (isWithinBua()) {
                You are in a built-up area,
              } @else {
                You are in a rural area,
              }
              zoom in to 1:{{ snapScaleLimit() | number }} to enable snapping.
            </p>
          }
          <mat-divider class="divider"></mat-divider>
          <div class="container">
            @for (segment of segments(); track $index) {
              <p class="item mat-label-medium">
                Segment {{ segment.label }}:

                {{ segment | segmentDistance | distance }}
              </p>
            }
          </div>
          <mat-divider class="divider"></mat-divider>
          <p data-testid="measured-total-distance" class="mat-label-large">
            Total distance:
            {{ vertices() | totalDistance | distance }}
          </p>
          @if (measureType() === MeasureType.AREA) {
            <p data-testid="measured-total-area" class="mat-label-large">
              Total area:
              <span [innerHtml]="vertices() | totalArea | area"></span>
            </p>
          }
          <p class="mat-label-small">
            Dimensions are approximate. For accurate property dimensions,
            undertake a Title search.
          </p>
        </mat-card-content>
        <mat-card-actions>
          <button mat-button color="error" (click)="onClear()">Clear</button>
          @if (isDrawing()) {
            <button mat-button (click)="onUndo()">Undo</button>
          }
        </mat-card-actions>
      </mat-card>
    }
  `,
  styles: `
    @use '@angular/material' as mat;
    @use 'theme' as theme;

    .measure-card {
      background: white;
      max-width: 320px;
    }

    .header {
      display: flex;
      flex-direction: row;
      align-items: center;

      h1 {
        font-size: 16px;
        margin: 0;
      }

      .close-button {
        position: absolute;
        top: 8px;
        right: 8px;
      }
    }

    .content {
      p {
        margin: 4px 0;
      }
    }

    .container {
      max-height: 250px;
      overflow: auto;
    }

    .divider {
      margin: 6px 0;
    }

    .snap-toggle-row {
      display: flex;
      align-items: center;
      gap: 8px;
      margin: 4px 0;
    }

    .error-hint {
      color: #{mat.get-theme-color(theme.$light-theme, error)};
      margin-top: 2px;
      max-width: 280px;
    }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [],
})
export class MeasureOverlayComponent implements OnDestroy {
  readonly measurementService = inject(MapMeasurementService);
  private readonly parcelSnapService = inject(ParcelSnapService);

  isDrawing = this.measurementService.isDrawing;
  measureType = this.measurementService.currentMeasureType;
  hasSelfIntersection = this.measurementService.hasSelfIntersection;
  snapEnabled = this.parcelSnapService.snapEnabled;
  isSnapLoading = this.parcelSnapService.isSnapLoading;
  isTooZoomedOut = this.parcelSnapService.isTooZoomedOut;
  isWithinBua = this.parcelSnapService.isWithinBua;
  snapScaleLimit = this.parcelSnapService.scaleLimit;
  isSnapDisabled = computed(
    () => this.isSnapLoading() || this.isTooZoomedOut(),
  );

  readonly MeasureType = MeasureType;

  vertices = computed(() =>
    this.measurementService.currentVertexes().map((vertex) => vertex.latlng),
  );
  segments = computed(() => {
    const vertices = this.measurementService.currentVertexes();
    return vertices.reduce((acc, cur, index, list) => {
      if (index === 0) {
        return [];
      }
      const from = list[index - 1];
      const to = cur;

      return [
        ...acc,
        {
          from: from.latlng,
          to: to.latlng,
          label: [from.id, to.id].join('-'),
        },
      ];
    }, [] as IMeasureSegment[]);
  });

  ngOnDestroy(): void {
    this.parcelSnapService.toggleSnap(false);
  }

  protected onClose() {
    this.parcelSnapService.toggleSnap(false);
    this.measurementService.abortDrawing();
    this.measurementService.clearDrawing();
  }

  protected onClear() {
    this.measurementService.resetMeasure();
  }

  protected onUndo() {
    this.measurementService.undo();
  }

  protected onSnapToggle(enabled: boolean) {
    this.parcelSnapService.toggleSnap(enabled);
  }
}
